'use client';

import {clsx} from '@gfiber/typesmith';
import {
  Checkbox as BaseCheckbox,
  type CheckboxRootChangeEventDetails,
} from '@base-ui/react/checkbox';
import * as React from 'react';
import {Ripple, type RippleRef} from '../../../components/Ripple/Ripple';
import styles from './Checkbox.module.scss';

export interface CheckboxV2Props extends BaseCheckbox.Root.Props {
  label?: React.ReactNode;
  disableRipple?: boolean;
  className?: string;
  alignLeft?: boolean;
}

const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxV2Props>(
  ({className, label, disableRipple, alignLeft = false, ...props}, ref) => {
    const generatedID = React.useId();
    const id = props.id ?? generatedID;
    const rippleRef = React.useRef<RippleRef>(null);

    const handleClick = () => {
      if (!disableRipple) {
        rippleRef.current?.trigger();
      }
    };

    const renderLabel = () => {
      if (!label) return null;
      return <span className={styles.label}>{label}</span>;
    };

    return (
      <label
        className={styles.container}
        data-disabled={props.disabled || undefined}
        htmlFor={id}
      >
        {alignLeft && renderLabel()}

        <BaseCheckbox.Root
          {...props}
          ref={ref}
          id={id}
          className={clsx(styles.checkbox, className)}
          onClick={e => {
            handleClick();
            props.onClick?.(e);
          }}
        >
          <div className={clsx(!disableRipple && styles.rippleContainer)}>
            {!disableRipple && <Ripple ref={rippleRef} />}
          </div>

          <BaseCheckbox.Indicator className={styles.indicator}>
            <svg
              viewBox="0 0 14 14"
              fill="none"
              className={styles.icon}
              aria-hidden={true}
            >
              <path
                d="M3 8L6 11L11 3.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </BaseCheckbox.Indicator>
        </BaseCheckbox.Root>

        {!alignLeft && renderLabel()}
      </label>
    );
  },
);

Checkbox.displayName = 'CheckboxV2';

export {Checkbox};
